import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PaymentPlanVersionReason } from '../enums/PaymentPlanVersionReason';

// 🆕 Enums y DTOs para nuevas modalidades de cobertura
export enum CoverageModality {
  Total = 1,      // 100% empresa
  Partial = 2     // X% empresa + participantes
}

export enum CoverageType {
  FULL = 'FULL',           // 100% empresa
  PERCENTAGE = 'PERCENTAGE', // % empresa
  FIXED = 'FIXED',          // Valor fijo empresa
  NONE = 'NONE'            // 100% participante
}

export interface CorporateEnrollmentParticipant {
  participantId: number;
  coverageType: string;
  companyCoveragePercent?: number;
  companyCoverageAmount?: number;
}

export interface CompanyPaymentPlan {
  dueDate: Date;
  amountToPay: number;
  installmentNumber: number;
  observations?: string;
  // Indica si se debe generar link de pago automático para esta cuota
  generateLink?: boolean;
}

export interface MasterPaymentPlan {
  dueDate: Date;
  amountToPay: number;
  installmentNumber: number;
  observations?: string;
}

export interface PaymentLinksInfo {
  companyPaymentLinks: number;
  participantPaymentLinks: number;
  totalPaymentLinks: number;
  generationType: string;
}

export interface CorporateEnrollmentRequest {
  companyId: number;
  projectId: number;
  coverageModality: CoverageModality;
  inscriptionAmount: number; // 🆕 Monto editable de la inscripción del programa
  defaultCompanyCoveragePercent: number;
  isFixedCoverage: boolean;
  startDate: Date;
  endDate?: Date;
  terms?: string;
  participants: CorporateEnrollmentParticipant[];
  companyPaymentPlans: CompanyPaymentPlan[]; // 🆕 Ahora requeridos
  masterPaymentPlans: MasterPaymentPlan[]; // 🆕 Requeridos para modalidad Parcial

  // 🌍 Campos para inscripciones corporativas internacionales
  currencyId?: number;          // ID de la moneda (1=COP, 2=USD, etc.)
  isInternational?: boolean;    // Indica si es una inscripción internacional

  // 📊 Estado inicial de la inscripción
  status?: number;              // 0 = Pending, 1 = Active

  // 🆕 Cobro automático vía email
  autoGeneratePaymentLinks?: boolean; // true = genera links automáticos, false = paga por otros medios

  // Observaciones generales del acuerdo corporativo
  observations?: string;
}

export interface CorporateEnrollmentResponse {
  agreementId: number;
  companyId: number;
  projectId: number;
  coverageModality: CoverageModality;
  createdEnrollments: number;
  enrollments: any[];
  paymentLinksInfo: PaymentLinksInfo;
  message: string;

  // 🌍 Campos para inscripciones corporativas internacionales
  currencyId?: number;          // ID de la moneda
  currencyCode?: string;        // Código de moneda (COP, USD, etc.)
  exchangeRate?: number;        // Tasa de cambio aplicada
  isInternational?: boolean;    // Indica si es una inscripción internacional
}

// 🆕 Interfaz para agregar participantes a inscripción corporativa existente
export interface AddParticipantsRequest {
  participants: CorporateEnrollmentParticipant[];
  updatedCompanyPlans?: CompanyPaymentPlan[];
}

@Injectable({ providedIn: 'root' })
export class CorporateEnrollmentService {
  private _baseUrl: string = '';
  private _controllerName: string = '';

  constructor(private _http: HttpClient) {
    this._baseUrl = environment.apiUrl;
    this._controllerName = 'CorporateEnrollment';
  }

  /**
   * 🆕 Agregar participantes a una inscripción corporativa existente
   */
  addParticipants(agreementId: number, request: AddParticipantsRequest): Observable<CorporateEnrollmentResponse> {
    return this._http
      .post<CorporateEnrollmentResponse>(`${this._baseUrl}/${this._controllerName}/${agreementId}/participants`, request)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * Crear inscripción corporativa con modalidades A/B
   */
  createCorporateEnrollment(payload: CorporateEnrollmentRequest): Observable<CorporateEnrollmentResponse> {
    return this._http
      .post<CorporateEnrollmentResponse>(`${this._baseUrl}/${this._controllerName}`, payload)
      .pipe(catchError(this.handleErrors));
  }

  /** Refinanciar plan de pagos de la empresa (requiere motivo + comentario) */
  refinanceCompany(
    agreementId: number,
    plans: CompanyPaymentPlan[],
    versionReason: PaymentPlanVersionReason = PaymentPlanVersionReason.Refinance,
    versionReasonComment: string = '',
    redistributionLog?: string
  ): Observable<any> {
    return this._http
      .post(`${this._baseUrl}/${this._controllerName}/${agreementId}/refinance/company`, {
        plans, versionReason, versionReasonComment, redistributionLog: redistributionLog ?? null
      })
      .pipe(catchError(this.handleErrors));
  }

  /** Refinanciar plan de pagos de participantes (aplicar plan maestro). Requiere motivo + comentario. */
  refinanceParticipants(
    agreementId: number,
    masterPlans: MasterPaymentPlan[],
    versionReason: PaymentPlanVersionReason = PaymentPlanVersionReason.Refinance,
    versionReasonComment: string = '',
    redistributionLog?: string
  ): Observable<any> {
    return this._http
      .post(`${this._baseUrl}/${this._controllerName}/${agreementId}/refinance/participants`, {
        plans: masterPlans, versionReason, versionReasonComment, redistributionLog: redistributionLog ?? null
      })
      .pipe(catchError(this.handleErrors));
  }

  /** Refinanciar plan de pagos de un participante específico (requiere motivo + comentario) */
  refinanceSingleParticipant(
    agreementId: number,
    participantId: number,
    plans: MasterPaymentPlan[],
    versionReason: PaymentPlanVersionReason = PaymentPlanVersionReason.Refinance,
    versionReasonComment: string = '',
    redistributionLog?: string
  ): Observable<any> {
    return this._http
      .post(`${this._baseUrl}/${this._controllerName}/${agreementId}/refinance/participants/${participantId}`, {
        plans, versionReason, versionReasonComment, redistributionLog: redistributionLog ?? null
      })
      .pipe(catchError(this.handleErrors));
  }

  /** Obtener cuotas editables del participante (PENDING o LINK_GENERATED) */
  getParticipantEditablePlans(agreementId: number, participantId: number): Observable<any> {
    return this._http
      .get(`${this._baseUrl}/${this._controllerName}/${agreementId}/participants/${participantId}/editable-plans`)
      .pipe(catchError(this.handleErrors));
  }

  /** Obtener detalles del acuerdo corporativo */
  getAgreementDetails(agreementId: number): Observable<any> {
    return this._http
      .get(`${this._baseUrl}/${this._controllerName}/${agreementId}/details`)
      .pipe(catchError(this.handleErrors));
  }

  /** 🆕 Contexto para el modal de edición de valor de matrícula por participante. */
  getInscriptionContext(agreementId: number): Observable<any> {
    return this._http
      .get(`${this._baseUrl}/${this._controllerName}/${agreementId}/inscription-context`)
      .pipe(catchError(this.handleErrors));
  }

  /** 🆕 Actualiza el valor de matrícula por participante y recalcula el descuento. */
  updateInscriptionAmount(agreementId: number, newTotalAmount: number, comment: string): Observable<any> {
    return this._http
      .patch(`${this._baseUrl}/${this._controllerName}/${agreementId}/inscription-amount`,
        { newTotalAmount, comment })
      .pipe(catchError(this.handleErrors));
  }

  /** 
   * 🆕 Obtener inscripciones corporativas de una empresa específica
   * Para el flujo de comprobantes donde solo la empresa paga
   */
  getCorporateEnrollmentsByCompany(companyId: number): Observable<any> {
    return this._http
      .get(`${this._baseUrl}/${this._controllerName}/company/${companyId}`)
      .pipe(catchError(this.handleErrors));
  }

  /** 
   * 🆕 Obtener inscripciones corporativas donde participa un estudiante específico
   * Para el flujo de comprobantes de "Participante de una empresa"
   */
  getCorporateEnrollmentsByParticipant(participantId: number): Observable<any> {
    return this._http
      .get(`${this._baseUrl}/${this._controllerName}/participant/${participantId}`)
      .pipe(catchError(this.handleErrors));
  }

  /**
   * 🆕 Anula una inscripción corporativa
   */
  cancel(agreementId: number, reason: string): Observable<any> {
    return this._http.put<any>(`${this._baseUrl}/${this._controllerName}/${agreementId}/cancel`, { cancellationReason: reason }).pipe(catchError(this.handleErrors));
  }

  /**
   * 🆕 Agrega (append) una nueva observación al acuerdo corporativo.
   * El backend la formatea con timestamp + usuario y concatena al campo Observations existente.
   * NO reemplaza la observación previa.
   */
  appendObservation(agreementId: number, note: string): Observable<any> {
    return this._http.patch<any>(
      `${this._baseUrl}/${this._controllerName}/${agreementId}/observations/append`,
      { note }
    ).pipe(catchError(this.handleErrors));
  }

  /**
   * Helper para validar si modalidad es Total
   */
  isTotalCoverageModality(modality: CoverageModality): boolean {
    return modality === CoverageModality.Total;
  }

  /**
   * Helper para validar si modalidad es Parcial
   */
  isPartialCoverageModality(modality: CoverageModality): boolean {
    return modality === CoverageModality.Partial;
  }

  /**
   * Calcular distribución de costos para participante
   */
  calculateCoverageDistribution(
    totalAmount: number,
    participant: CorporateEnrollmentParticipant,
    defaultPercent: number
  ): { companyAmount: number, participantAmount: number, companyPercent: number, participantPercent: number } {

    let companyPercent = 0;
    let companyAmount = 0;

    switch (participant.coverageType) {
      case CoverageType.FULL:
        companyPercent = 100;
        break;
      case CoverageType.PERCENTAGE:
        companyPercent = participant.companyCoveragePercent || defaultPercent;
        break;
      case CoverageType.FIXED:
        companyAmount = participant.companyCoverageAmount || 0;
        companyPercent = (companyAmount / totalAmount) * 100;
        break;
      case CoverageType.NONE:
        companyPercent = 0;
        break;
      default:
        companyPercent = defaultPercent;
    }

    if (participant.coverageType !== CoverageType.FIXED) {
      companyAmount = Math.round(totalAmount * (companyPercent / 100));
    }

    const participantAmount = totalAmount - companyAmount;
    const participantPercent = 100 - companyPercent;

    return {
      companyAmount,
      participantAmount,
      companyPercent,
      participantPercent
    };
  }

  private handleErrors(error: HttpErrorResponse) {
    let errorMessage = 'Algo salió mal; por favor, intente nuevamente más tarde.';

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      console.log('Ocurrió un error del cliente:', error.error.message);
      errorMessage = error.error.message;
    } else {
      // Error del lado del servidor
      console.log(`Error del servidor ${error.status}:`, error.error);

      // Extraer mensaje de error del backend (puede ser string o objeto)
      if (error.error) {
        if (typeof error.error === 'string') {
          errorMessage = error.error;
        } else if (error.error.message) {
          errorMessage = error.error.message;
        } else if (error.error.errorMessage) {
          errorMessage = error.error.errorMessage;
        } else if (error.error.title) {
          errorMessage = error.error.title;
        }
      }
    }

    return throwError(() => ({ message: errorMessage, status: error.status, originalError: error.error }));
  }
}


